import type { BiomarkerValue } from '@/api/generated-catch-backend/model';

interface BiomarkerReferenceRangeProps {
  resultValue: BiomarkerValue;
}

export function BiomarkerReferenceRange({ resultValue }: BiomarkerReferenceRangeProps) {
  const { referenceRangeMin, referenceRangeMax, numericValue } = resultValue;

  // If we don't have the necessary data for visualization, don't render
  if (!referenceRangeMin || !referenceRangeMax || !numericValue) {
    return null;
  }

  // Calculate position percentage for the user's value
  const range = referenceRangeMax - referenceRangeMin;
  const userPosition = ((numericValue - referenceRangeMin) / range) * 100;

  // Clamp position between 0 and 100
  const clampedPosition = Math.max(0, Math.min(100, userPosition));

  return (
    <div className="rounded-lg bg-[#F0F5F6] px-5 py-6">
      <div className="relative flex items-start gap-0">
        {/* Left out-of-range zone (salmon) - larger proportion */}
        <div className="flex flex-[2] flex-col items-start pb-0 pt-5">
          <div className="flex w-full flex-1 flex-col rounded-sm bg-[rgba(245,159,0,0.2)]">
            <div className="h-1.5 w-full rounded-sm bg-catch-salmon" />
          </div>
        </div>

        {/* Min boundary marker */}
        <div className="flex flex-col items-center justify-center gap-1">
          <p className="text-xs font-normal leading-snug text-catch-dark-blue-90">{referenceRangeMin}</p>
          <div className="h-8 w-px border-l border-dashed border-gray-400" />
        </div>

        {/* Normal range - left blue zone */}
        <div className="flex flex-[1] flex-col items-start pb-0 pt-5">
          <div className="flex w-full flex-1 flex-col rounded-sm bg-[rgba(245,159,0,0.2)]">
            <div className="h-1.5 w-full rounded-sm bg-catch-light-blue" />
          </div>
        </div>

        {/* Optimal range - green zone */}
        <div className="flex flex-[1] flex-col items-start pb-0 pt-5">
          <div className="flex w-full flex-1 flex-col rounded-sm bg-[rgba(245,159,0,0.2)]">
            <div className="h-1.5 w-full rounded-sm bg-catch-avocado-400" />
          </div>
        </div>

        {/* Normal range - right blue zone */}
        <div className="flex flex-[1] flex-col items-start pb-0 pt-5">
          <div className="flex w-full flex-1 flex-col rounded-sm bg-[rgba(245,159,0,0.2)]">
            <div className="h-1.5 w-full rounded-sm bg-catch-light-blue" />
          </div>
        </div>

        {/* Max boundary marker */}
        <div className="flex flex-col items-center justify-center gap-1">
          <p className="text-xs font-normal leading-snug text-catch-dark-blue-90">{referenceRangeMax}</p>
          <div className="h-8 w-px border-l border-dashed border-gray-400" />
        </div>

        {/* Right out-of-range zone (salmon) - larger proportion */}
        <div className="flex flex-[2] flex-col items-start pb-0 pt-5">
          <div className="flex w-full flex-1 flex-col rounded-sm bg-[rgba(245,159,0,0.2)]">
            <div className="h-1.5 w-full rounded-sm bg-catch-salmon" />
          </div>
        </div>

        {/* User's value marker - positioned based on actual value */}
        <div
          className="absolute top-[-1px] flex -translate-x-1/2 flex-col items-center justify-center gap-1"
          style={{ left: `${clampedPosition}%` }}
        >
          <div className="flex items-center justify-center rounded border border-catch-dark-blue-90 bg-catch-dark-blue-90 px-1.5">
            <p className="text-xs font-bold leading-snug text-white">{numericValue}</p>
          </div>
          <div className="h-8 w-px border-l-2 border-catch-dark-blue-90" />
        </div>
      </div>
    </div>
  );
}
